import { useEffect, useId, useMemo, useState } from 'react';
import plur from 'plur';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  CodeBlock,
  InlineStack,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Stack,
  Text,
  useModal,
} from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { hydraUiApi } from '@/services/api';
import { getUserFacingErrorMessage } from '@/services/api';
import { snackbarApi } from '@/services/snackbar';
import { sentryCaptureException } from '@/utils/sentry';
import { resolveCopilotWorkspaceId } from '../workspace-context';
import { z } from '@customerio/hydra-validation';
import { QueryBuilderForm } from './query-builder-form';
import { changedLines, describeEdits, formToSpec, rerunMessage, specToForm } from './spec-form';
import type { Condition, QueryForm, QuerySpec, SpecValue } from './spec-form';
import { findViewCatalog } from './view-catalog';
import * as styles from './query-builder.css';

export interface QueryBuilderModalData {
  /** The customer's question, as they asked it. */
  question: string;
  /** The analytics query spec the agent sent to snowflake_query, as JSON text. */
  spec: string;
  /**
   * The workspace the agent ran the query in. A conversation is not bound to
   * one workspace, so without this the modal can only use the page's workspace,
   * which may not be where the answer's numbers came from.
   */
  workspaceId: number | null;
  /**
   * Posts a user turn to the conversation the answer is in, so an edited query
   * becomes a new answer there, with the agent's reading and a fresh link.
   * Null when the link is rendered outside a conversation.
   */
  send: ((message: string) => Promise<void>) | null;
}

type QueryResult = {
  /** The spec that produced these rows, so they never show under another. */
  specJson: string;
  columns: string[];
  rowCount: number;
  /** The rows as pretty-printed JSON; a table replaces this in a later slice. */
  rowsJson: string;
  truncated: boolean;
  sql: string;
};

// The server validates the full spec; the client parses the v1 shape the form
// reads and keeps unknown keys at every level, so nothing the agent sent is
// dropped.
const scalarSchema = z.union([z.string(), z.number(), z.boolean()]);
const specValueSchema: z.ZodType<SpecValue> = z.union([
  scalarSchema,
  z.array(scalarSchema),
  z.object({ relative: z.object({ n: z.number(), unit: z.string() }).loose() }).loose(),
]);

const conditionSchema: z.ZodType<Condition> = z.lazy(() =>
  z
    .object({
      and: z.array(conditionSchema).optional(),
      or: z.array(conditionSchema).optional(),
      field: z.string().optional(),
      op: z.string().optional(),
      value: specValueSchema.optional(),
    })
    .loose(),
);

const querySpecSchema: z.ZodType<QuerySpec> = z
  .object({
    version: z.literal(1),
    select: z.array(z.string()).optional(),
    from: z.object({ view: z.string(), metrics: z.array(z.string()), dimensions: z.array(z.string()) }).loose(),
    where: conditionSchema.optional(),
    group_by: z
      .object({
        columns: z.array(z.string()).optional(),
        time_bucket: z.object({ field: z.string(), unit: z.string() }).loose().optional(),
      })
      .loose()
      .optional(),
    order_by: z.array(z.object({ field: z.string(), dir: z.string() }).loose()).optional(),
    limit: z.number().optional(),
  })
  .loose();

function parseSpec(text: string): QuerySpec | null {
  try {
    const parsed = querySpecSchema.safeParse(JSON.parse(text));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

type SqlPreviewProps = { sql: string; baseline: string | null; labelledBy: string };

/**
 * The compiled SQL, with the lines the agent's query did not have marked.
 * Not a live region: it is rewritten on every edit, and reading it all out each
 * time would drown the form. The "N lines changed" status announces instead.
 * Focusable, because a long line scrolls sideways and a keyboard user must be
 * able to scroll it.
 */
function SqlPreview({ sql, baseline, labelledBy }: SqlPreviewProps) {
  const changed = baseline == null ? new Set<number>() : changedLines(baseline, sql).addedAt;
  return (
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scrollable region must be keyboard-reachable (WCAG 2.1.1)
    <pre className={styles.sqlPane} tabIndex={0} aria-labelledby={labelledBy}>
      {sql.split('\n').map((line, i) => (
        <span key={i} className={changed.has(i) ? `${styles.sqlLine} ${styles.sqlLineChanged}` : styles.sqlLine}>
          {line}
        </span>
      ))}
    </pre>
  );
}

/**
 * Shows the query behind an analytics answer as a form, with the SQL the
 * server compiles from it, and runs it again.
 *
 * The SQL is never built here: every edit changes the spec, and a dry run
 * returns the SQL for that spec without touching Snowflake. The lines that
 * differ from the agent's query are marked, so the customer sees exactly what
 * an edit does before running it.
 */
export function QueryBuilderModal({ modalState, data, onClose }: PlumaManagedModalProps<QueryBuilderModalData>) {
  return (
    <Modal state={modalState} onClose={onClose} title="Query behind this answer" size="md" className={styles.modal}>
      {/* Keyed on the answer, so a different answer's link starts the form and
          its queries from scratch instead of resetting state in an effect. */}
      <QueryBuilderBody key={`${data.workspaceId ?? ''}|${data.spec}`} data={data} onClose={onClose} />
    </Modal>
  );
}

type QueryBuilderBodyProps = { data: QueryBuilderModalData; onClose: () => void };

function QueryBuilderBody({ data, onClose }: QueryBuilderBodyProps) {
  // The query runs where the answer came from. An answer without a workspace
  // (an agent that predates the workspaceId argument) falls back to the
  // sidebar's current workspace, and the modal says so when that differs from
  // the page.
  const pageWorkspaceId = resolveCopilotWorkspaceId();
  const workspaceId = data.workspaceId ?? pageWorkspaceId;
  const workspaceNote =
    workspaceId == null
      ? null
      : data.workspaceId == null
        ? 'This answer did not record its workspace; the query runs in the current one.'
        : pageWorkspaceId != null && pageWorkspaceId !== workspaceId
          ? `Runs in workspace ${workspaceId}, where the answer came from, not the one open on this page.`
          : null;
  const sqlLabelId = useId();
  const original = useMemo(() => parseSpec(data.spec), [data.spec]);
  const catalog = original == null ? null : findViewCatalog(original.from.view);

  const [form, setForm] = useState<QueryForm | null>(() => (original == null ? null : specToForm(original)));
  // Bumped by Reset to remount the form, so controls that keep their own text
  // (a comma-separated list) start over from the agent's query too.
  const [resetCount, setResetCount] = useState(0);

  const edited = useMemo(() => (form == null ? null : formToSpec(form)), [form]);
  const editedJson = edited == null ? '' : JSON.stringify(edited);
  const originalJson = original == null ? '' : JSON.stringify(original);
  // Compared through the same mapping, so the agent's key order never reads as an edit.
  const isEdited = original != null && editedJson !== JSON.stringify(formToSpec(specToForm(original)));
  // Until something is edited the agent's spec runs as parsed, so "Run again"
  // means exactly that and the form's mapping cannot change the query.
  const spec = isEdited ? edited : original;
  const specJson = isEdited ? editedJson : originalJson;

  const canCompile = spec != null && workspaceId != null;

  // A null spec never reaches the server: the queries below are disabled for
  // it, and the throw only satisfies the query function's contract.
  const dryRun = async (body: QuerySpec | null) => {
    if (body == null) throw new Error('no spec to compile');
    const response = await hydraUiApi.snowflake.snowflakeQuery({
      params: { path: { environment_id: String(workspaceId) } },
      body: { spec: body, dry_run: true },
    });
    return response.sql;
  };

  // The agent's query, compiled once; the baseline the edited SQL is marked against.
  const baseline = useQuery({
    queryKey: ['query-builder', 'dry-run', workspaceId, originalJson],
    enabled: original != null && workspaceId != null,
    queryFn: () => dryRun(original),
  });

  const preview = useQuery({
    queryKey: ['query-builder', 'dry-run', workspaceId, specJson],
    enabled: canCompile,
    queryFn: () => dryRun(spec),
    placeholderData: (previous) => previous,
  });

  // The result carries the spec it came from and renders only while that is
  // still the spec on screen: an edit made while a run is in flight must not
  // put the old rows under the new SQL.
  const run = useMutation({
    mutationFn: async (): Promise<QueryResult> => {
      const response = await hydraUiApi.snowflake.snowflakeQuery({
        params: { path: { environment_id: String(workspaceId) } },
        body: { spec },
      });
      return {
        specJson,
        columns: response.columns,
        rowCount: response.rows.length,
        rowsJson: JSON.stringify(response.rows, null, 2),
        truncated: response.truncated,
        sql: response.sql,
      };
    },
  });
  const result = run.data != null && run.data.specJson === specJson ? run.data : null;

  // An error from a previous spec must not linger under a new one.
  const { reset: resetRun } = run;
  useEffect(() => {
    resetRun();
  }, [specJson, resetRun]);

  const runError = run.isError ? getUserFacingErrorMessage(run.error, 'The query could not be run.') : null;

  // Sending hands the edited spec to the agent as the customer's next turn and
  // closes the modal at once; the answer, with its own link, arrives in the
  // conversation. Not awaited: the session's promise settles only when the
  // agent's whole turn ends, and the conversation reports a failed turn itself.
  // A rejection may come before the turn starts (session ended, not
  // configured), when nothing else shows it, or after, when the conversation
  // shows the failed turn too; the toast is worded for both. The turn names the
  // workspace unless it is known to be the page's own, so the agent runs it there.
  const { send } = data;
  const sendToConversation = () => {
    if (send == null || edited == null || original == null || form == null || workspaceId == null) {
      throw new Error('nothing to send: the button renders only for an edited query in a conversation');
    }
    const elsewhere = pageWorkspaceId === workspaceId ? undefined : workspaceId;
    const message = rerunMessage(data.question, describeEdits(specToForm(original), form), edited, elsewhere);
    send(message).catch((error: unknown) => {
      sentryCaptureException(error);
      snackbarApi.create({
        variant: 'critical',
        id: 'copilot.query-builder.send-failed',
        message: 'The edited query may not have reached the conversation. Check before sending again.',
      });
    });
    onClose();
  };
  const changed =
    baseline.data != null && preview.data != null && isEdited ? changedLines(baseline.data, preview.data) : null;
  const changedCount = changed == null ? 0 : changed.added + changed.removed;

  return (
    <>
      <ModalBody className={styles.body}>
        <Stack gap="400">
          <Paragraph>
            <Text color="secondary">You asked: </Text>
            <Text weight="medium">{data.question}</Text>
          </Paragraph>

          {original == null ? (
            <Text color="critical">The query could not be read. Ask the Agent to run it again.</Text>
          ) : workspaceId == null ? (
            <Text color="critical">Open a workspace to compile and run this query.</Text>
          ) : workspaceNote != null ? (
            <Text size="sm" color="caution">
              {workspaceNote}
            </Text>
          ) : null}

          <div className={styles.columns}>
            {form != null ? (
              <QueryBuilderForm key={resetCount} form={form} catalog={catalog} onChange={setForm} />
            ) : (
              <div />
            )}

            <Stack gap="200">
              <Stack gap="100">
                <InlineStack gap="100" alignBlock="center" alignInline="space-between">
                  <Text id={sqlLabelId} size="sm" color="secondary">
                    SQL that will run
                  </Text>
                  {/* Always mounted, so screen readers hear each change in the count. */}
                  <div role="status" aria-live="polite">
                    {preview.isFetching && preview.data != null ? (
                      <Text size="xs" color="secondary">
                        Compiling…
                      </Text>
                    ) : changedCount > 0 ? (
                      <Text size="xs" color="caution" weight="medium">
                        {changedCount} {plur('line', changedCount)} changed from the Agent's query
                      </Text>
                    ) : isEdited ? (
                      <Text size="xs" color="caution" weight="medium">
                        edited
                      </Text>
                    ) : (
                      <Text size="xs" color="secondary">
                        no changes
                      </Text>
                    )}
                  </div>
                </InlineStack>
                {preview.isPending && canCompile ? (
                  <Text size="sm" color="secondary">
                    Compiling…
                  </Text>
                ) : preview.isError ? (
                  <Text size="sm" color="critical" role="alert">
                    {getUserFacingErrorMessage(preview.error, 'The query could not be compiled.')}
                  </Text>
                ) : preview.data != null ? (
                  <SqlPreview sql={preview.data} baseline={baseline.data ?? null} labelledBy={sqlLabelId} />
                ) : null}
              </Stack>

              {result != null ? (
                <Stack gap="100">
                  <Text size="sm" color="secondary" role="status">
                    {result.rowCount} {plur('row', result.rowCount)}
                    {result.truncated ? ' (truncated at the row cap)' : ''}
                  </Text>
                  <CodeBlock code={result.rowsJson} language="json" withLineNumbers={false} />
                </Stack>
              ) : null}

              {runError != null ? (
                <Text size="sm" color="critical" role="alert">
                  {runError}
                </Text>
              ) : null}
            </Stack>
          </div>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="secondary"
          isDisabled={!isEdited || original == null}
          onClick={() => {
            if (original == null) return;
            setForm(specToForm(original));
            setResetCount((count) => count + 1);
          }}
        >
          Reset query
        </Button>
        <Box flexGrow="1" />
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={isEdited && send != null ? 'secondary' : 'primary'}
          onClick={() => run.mutate()}
          isLoading={run.isPending}
          isDisabled={!canCompile || preview.isError}
        >
          {isEdited ? 'Preview' : 'Run again'}
        </Button>
        {isEdited && send != null ? (
          <Button variant="primary" onClick={sendToConversation} isDisabled={!canCompile || preview.isError}>
            Send to conversation
          </Button>
        ) : null}
      </ModalFooter>
    </>
  );
}

export function useQueryBuilderModal() {
  return useModal(QueryBuilderModal);
}
